/**
 * Phone-number parsing, normalisation and display.
 *
 * One contract, implemented identically by the API and the Admin app (all
 * three pin libphonenumber-js to the same version, "max" metadata):
 *
 *   normalizePhone(input, { defaultCountry = 'BB' })
 *     → { ok: true, e164, country, extension, national }
 *     → { ok: false, code, message }
 *       code ∈ EMPTY | TOO_SHORT | TOO_LONG | INVALID_COUNTRY | INVALID | NOT_A_NUMBER
 *
 *   formatPhoneDisplay(e164, { extension, defaultCountry = 'BB' }) → string
 *
 * A number is accepted when it parses and is POSSIBLE (length and structure
 * for its country), not when it is a currently assigned number: the plan's
 * own example, 246 555 1234, uses an unassigned exchange and must normalise.
 *
 * Barbados is the default country — the only market the product operates in
 * (BBD-only pricing, America/Barbados). Any other country is accepted when
 * the number is entered internationally (+44 …) or the country is chosen.
 *
 * The extension is never part of `e164`; it is returned on its own.
 */
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js/max';

export const DEFAULT_PHONE_COUNTRY = 'BB';

export const PHONE_ERROR_MESSAGES = {
  EMPTY: 'Enter a phone number.',
  TOO_SHORT: 'This number is too short. Check the number and try again.',
  TOO_LONG: 'This number is too long. Check the number and try again.',
  INVALID_COUNTRY: 'That country code is not recognised. Choose the country, or start the number with + and its country code.',
  NOT_A_NUMBER: 'Enter the number using digits, for example 246 555 1234.',
  INVALID: 'This phone number is not valid for the selected country. Check the number and the country.',
};

const LENGTH_CODES = new Set(['TOO_SHORT', 'TOO_LONG', 'INVALID_COUNTRY', 'NOT_A_NUMBER']);

function fail(code) {
  return { ok: false, code, message: PHONE_ERROR_MESSAGES[code] };
}

// The country of a parsed number. Countries that share a calling code (+1)
// are told apart by the number itself; when it does not say (an unassigned
// range), the caller's country is kept if it is one of the candidates.
function countryOf(parsed, defaultCountry) {
  if (parsed.country) return parsed.country;
  const possible = parsed.getPossibleCountries();
  if (possible.includes(defaultCountry)) return defaultCountry;
  return possible[0] || null;
}

export function normalizePhone(input, { defaultCountry = DEFAULT_PHONE_COUNTRY } = {}) {
  const text = typeof input === 'string' ? input.trim() : '';
  if (!text) return fail('EMPTY');
  if (!isSupportedCountry(defaultCountry)) return fail('INVALID_COUNTRY');

  const parsed = parsePhoneNumberFromString(text, { defaultCountry, extract: false });
  if (parsed && parsed.isPossible()) {
    return {
      ok: true,
      e164: parsed.number,
      country: countryOf(parsed, defaultCountry),
      extension: parsed.ext || null,
      // National format of the number alone — the extension stays separate.
      national: parsePhoneNumberFromString(parsed.number).formatNational(),
    };
  }

  const lengthCode = validatePhoneNumberLength(text, defaultCountry);
  return fail(LENGTH_CODES.has(lengthCode) ? lengthCode : 'INVALID');
}

/**
 * Readable form of a stored number: international format ("+1 246 555 1234"),
 * plus " ext. 123" when there is an extension. A legacy stored value that
 * does not parse is shown exactly as stored.
 */
export function formatPhoneDisplay(e164, { extension, defaultCountry = DEFAULT_PHONE_COUNTRY } = {}) {
  if (e164 === null || e164 === undefined) return '';
  const stored = String(e164);
  if (!stored.trim()) return '';
  const parsed = isSupportedCountry(defaultCountry)
    ? parsePhoneNumberFromString(stored, { defaultCountry, extract: false })
    : parsePhoneNumberFromString(stored, { extract: false });
  if (!parsed || !parsed.isPossible()) return stored;
  const ext = extension || parsed.ext;
  const number = parsePhoneNumberFromString(parsed.number).formatInternational();
  return ext ? `${number} ext. ${ext}` : number;
}

// ── Country list for the selector ────────────────────────────────────
// Barbados first, then the other Caribbean countries on the +1 plan with
// the United States, Canada and the United Kingdom, then everything else.
const NEARBY_COUNTRIES = [
  'AG', 'AI', 'BS', 'BM', 'VG', 'KY', 'DM', 'DO', 'GD', 'JM', 'MS', 'PR',
  'KN', 'LC', 'VC', 'SX', 'TT', 'TC', 'VI',
  'US', 'CA', 'GB',
];

let regionNames;
function countryName(code) {
  try {
    regionNames = regionNames || new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}

function toOption(code) {
  return { code, name: countryName(code), callingCode: getCountryCallingCode(code) };
}

let countryOptions;
/**
 * → { primary: [...], other: [...] }, each `{ code, name, callingCode }`.
 * `primary` keeps its curated order; `other` is alphabetical by name.
 */
export function getPhoneCountryOptions() {
  if (!countryOptions) {
    const primaryCodes = [DEFAULT_PHONE_COUNTRY, ...NEARBY_COUNTRIES].filter((code) => isSupportedCountry(code));
    const taken = new Set(primaryCodes);
    countryOptions = {
      primary: primaryCodes.map(toOption),
      other: getCountries()
        .filter((code) => !taken.has(code))
        .map(toOption)
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }
  return countryOptions;
}

export function phoneCallingCode(country) {
  return isSupportedCountry(country) ? getCountryCallingCode(country) : null;
}
